import { RuntimeThemeParameters, ThemeParameters } from '../types';
import { DeepReadonly } from '../../util/utility-types';

import colors from '../colors';

import {
  stateStandard,
  stateStandardContent,
  stateStandardNova,
  stateStandardContentNova,
} from '../states/standard-states';
import {
  statePrimary,
  statePrimaryContent,
  statePrimaryNova,
  statePrimaryContentNova,
} from '../states/primary-states';
import {
  stateSecondary,
  stateSecondaryToggle,
  stateSecondaryNova,
  stateSecondaryToggleNova,
} from '../states/secondary-states';
import { stateFocus, stateFocusNova } from '../states/focus';
import {
  stateStatus,
  stateStatusPadBox,
  stateStatusContent,
  stateStatusNova,
  stateStatusPadBoxNova,
  stateStatusContentNova,
} from '../states/status';
import { stateInputNova } from '../states/input-states';

import {
  backgrounds,
  backgroundDepths,
  backgroundDepthsNova,
  backgroundsNova,
} from '../backgrounds';
import { textTokens, textTokensNova } from '../text';
import {
  fontFamilyTokens,
  fontFamilyTokensNova,
  fontLetterSpacingTokens,
  fontLetterSpacingTokensNova,
  fontOverflowWrapTokens,
  fontOverflowWrapTokensNova,
  fontSizeTokens,
  fontSizeTokensNova,
  fontStyleTokens,
  fontStyleTokensNova,
  fontWeightTokens,
  fontWeightTokensNova,
  fontWordBreakTokens,
  fontWordBreakTokensNova,
  fontWordSpacingTokens,
  fontWordSpacingTokensNova,
  lineHeightTokens,
  lineHeightTokensNova,
  textTransformTokens,
  textTransformTokensNova,
} from '../typography';
import { spacing } from '../spacing';
import { animation } from '../animation';

export type ReactDesignTokens = ReturnType<typeof renderReactDesignTokens>;

export function renderReactDesignTokens(
  theme: ThemeParameters,
  parameters: RuntimeThemeParameters
) {
  const dt = {
    // New Tokens
    stateStandardNova: callStructWithArgs(stateStandardNova, theme),
    stateStandardContentNova: callStructWithArgs(stateStandardContentNova, theme),

    statePrimaryNova: callStructWithArgs(statePrimaryNova, theme),
    statePrimaryContentNova: callStructWithArgs(statePrimaryContentNova, theme),

    stateSecondaryNova: callStructWithArgs(stateSecondaryNova, theme),
    stateSecondaryToggleNova: callStructWithArgs(stateSecondaryToggleNova, theme),

    stateStatusNova: callStructWithArgs(stateStatusNova, theme),
    stateStatusPadBoxNova: callStructWithArgs(stateStatusPadBoxNova, theme),
    stateStatusContentNova: callStructWithArgs(stateStatusContentNova, theme),

    backgroundsDepthNova: callStructWithArgs(backgroundDepthsNova, theme),
    backgroundsNova: callStructWithArgs(backgroundsNova, theme),

    stateInputNova: callStructWithArgs(stateInputNova, theme),

    stateFocusNova: callStructWithArgs(stateFocusNova, theme),

    textNova: {
      selection: textTokensNova.selection(theme),
    },
    spacing,
    animation,

    fontNova: {
      family: fontFamilyTokensNova(theme, parameters.lang),
      letterSpacing: fontLetterSpacingTokensNova(),
      overflowWrap: fontOverflowWrapTokensNova(),
      size: fontSizeTokensNova(theme, parameters.lang),
      style: fontStyleTokensNova(theme, parameters.lang),
      weight: fontWeightTokensNova(parameters.lang),
      wordBreak: fontWordBreakTokensNova(parameters.lang),
      wordSpacing: fontWordSpacingTokensNova(),
      lineHeight: lineHeightTokensNova(parameters.lang),
      textTransform: textTransformTokensNova(parameters.lang),
    },

    // Old Tokens
    stateStandard: callStructWithArgs(stateStandard, theme),
    stateStandardContent: callStructWithArgs(stateStandardContent, theme),

    statePrimary: callStructWithArgs(statePrimary, theme),
    statePrimaryContent: callStructWithArgs(statePrimaryContent, theme),

    stateSecondary: callStructWithArgs(stateSecondary, theme),
    stateSecondaryToggle: callStructWithArgs(stateSecondaryToggle, theme),

    stateFocus: callStructWithArgs(stateFocus, theme),

    stateStatus: callStructWithArgs(stateStatus, theme),
    stateStatusPadBox: callStructWithArgs(stateStatusPadBox, theme),
    stateStatusContent: callStructWithArgs(stateStatusContent, theme),

    backgrounds: callStructWithArgs(backgrounds, theme),
    backgroundsDepth: callStructWithArgs(backgroundDepths, theme),

    font: {
      family: fontFamilyTokens(theme, parameters.lang),
      letterSpacing: fontLetterSpacingTokens(),
      overflowWrap: fontOverflowWrapTokens(),
      size: fontSizeTokens(theme),
      style: fontStyleTokens(),
      weight: fontWeightTokens(),
      wordBreak: fontWordBreakTokens(),
      wordSpacing: fontWordSpacingTokens(),
      lineHeight: lineHeightTokens(),
      textTransform: textTransformTokens(),
    },

    text: {
      basic: textTokens.basic(theme),
      description: textTokens.description(theme),
      descriptionDisabled: textTokens.descriptionDisabled(theme),
      unread: textTokens.unread(theme),
      highlighted: textTokens.highlighted(theme),
      highlightedInFocus: textTokens.highlightedInFocus(theme),
      selection: textTokens.selection(theme),
    },

    enableNovaTokens: theme.enableNovaTokens,

    overrides: {
      banner: {
        bg: theme.overrides?.bannerBg,
        fg: theme.overrides?.bannerFg,
        img: theme.overrides?.bannerImg,
      },
      splash: {
        bg: theme.overrides?.splashBg,
        fg: theme.overrides?.splashFg,
        img: theme.overrides?.splashImg,
      },
      transition: {
        bg: theme.overrides?.transitionBg,
        fg: theme.overrides?.transitionFg,
        img: theme.overrides?.transitionImg,
      },
      table: {
        borderColor: theme.overrides?.tableBorderColor,
        cellBorderColor: theme.overrides?.tableCellBorderColor,
        headerBackground: theme.overrides?.tableHeaderBackground,
        headerBorderRightColor: theme.overrides?.tableHeaderBorderRightColor,
        headerLabelColor: theme.overrides?.tableHeaderLabelColor,
        rowBackgroundColorEven: theme.overrides?.tableRowBackgroundColorEven,
        rowBackgroundColorOdd: theme.overrides?.tableRowBackgroundColorOdd,
        textColor: theme.overrides?.tableTextColor,
        totalsBackgroundColor: theme.overrides?.tableTotalsBackgroundColor,
        totalsTextColor: theme.overrides?.tableTotalsTextColor,
      },
    },

    // #region EVIL
    c: callStructWithArgs(colors, theme),

    themeVariant: theme.baseThemeName,
    baseReportThemeName: theme.baseReportThemeName,
    // #endregion EVIL
  };

  return dt as DeepReadonly<typeof dt>;
}

export function callStructWithArgs<As extends any[], R, K extends string>(
  struct: { [P in K]: (...args: As) => R },
  ...args: As
) {
  const boundStruct: { [P in K]: R } = {} as any;

  for (const key of Object.keys(struct)) {
    (boundStruct as any)[key] = (struct as any)[key](...args);
  }

  return boundStruct;
}
